import React, { ReactNode, useEffect, useState } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, LayoutDashboard, LogOut, Settings, Plus, Tag, Map, Home, Users, Mail, FileDown, Building, Database, UploadCloud, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface AdminLayoutProps {
  children: ReactNode;
  title: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title }) => {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Chiude il drawer quando cambia pagina: altrimenti dopo un tap su una voce
  // il menu resterebbe aperto sopra il contenuto appena caricato.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Blocca lo scroll del body e chiude con Esc mentre il drawer è aperto.
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMenuOpen]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  const menuItems = [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/admin/properties', label: 'Immobili', icon: Building2 },
    { to: '/admin/properties/new', label: 'Nuovo Immobile', icon: Plus, forceReload: true },
    { to: '/admin/features', label: 'Dotazioni', icon: Tag },
    { to: '/admin/property-types', label: 'Tipologie Immobili', icon: Building },
    { to: '/admin/zones', label: 'Zone', icon: Map },
    { to: '/admin/xml-export', label: 'Esportazione Portali', icon: FileDown },
    { to: '/admin/contacts', label: 'Contatti', icon: Mail },
    { to: '/admin/users', label: 'Utenti', icon: Users },
    { to: '/admin/cms', label: 'CMS', icon: Database },
    { to: '/admin/settings', label: 'Impostazioni', icon: Settings },
    // { to: '/admin/migrazione-cdn', label: 'Migrazione CDN', icon: UploadCloud },  // nascosto post-go-live
  ];

  const handleSignOut = async () => {
    await signOut();
  };

  const linkClasses = (to: string) =>
    `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
      location.pathname === to
        ? 'bg-white/20 text-white'
        : 'text-white/70 hover:bg-white/10 hover:text-white'
    }`;

  // Contenuto condiviso fra sidebar desktop e drawer mobile.
  const sidebarContent = (
    <>
      <div className="mb-8 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <img src="/logo_molinaro.png" alt="Molinaro Immobiliare" className="h-10 brightness-0 invert" />
        </Link>
        <button
          type="button"
          onClick={() => setMobileMenuOpen(false)}
          className="md:hidden p-2 -mr-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors"
          aria-label="Chiudi menu"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <nav className="space-y-1 flex-1">
        {menuItems.map((item) => {
          // If we need to force reload for this item, use a regular <a> tag instead of Link
          if (item.forceReload) {
            return (
              <a key={item.to} href={item.to} className={linkClasses(item.to)}>
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span>{item.label}</span>
              </a>
            );
          }

          // Otherwise use the regular Link component
          return (
            <Link key={item.to} to={item.to} className={linkClasses(item.to)}>
              <item.icon className="h-5 w-5 flex-shrink-0" />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="mt-6 pt-6 border-t border-white/20">
        <Link to="/" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors mb-1">
          <Home className="h-5 w-5 flex-shrink-0" />
          <span>Vai al Sito</span>
        </Link>
        <button
          onClick={handleSignOut}
          className="flex items-center space-x-3 px-4 py-3 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors w-full"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          <span>Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar desktop: sempre visibile da md in su */}
      <motion.aside
        initial={{ x: -300 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.3 }}
        className="hidden md:flex md:flex-col w-64 bg-primary text-white p-6 fixed inset-y-0 left-0 overflow-y-auto z-30"
      >
        {sidebarContent}
      </motion.aside>

      {/* Drawer mobile + backdrop */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileMenuOpen(false)}
              className="md:hidden fixed inset-0 bg-black/50 z-40"
              aria-hidden="true"
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="md:hidden fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-primary text-white p-6 overflow-y-auto z-50 flex flex-col"
              role="dialog"
              aria-modal="true"
              aria-label="Menu di navigazione"
            >
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="md:ml-64">
        <header className="bg-white shadow-sm py-3 px-4 md:py-4 md:px-6 sticky top-0 z-20">
          <div className="flex justify-between items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="md:hidden p-2 -ml-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
                aria-label="Apri menu"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="text-lg md:text-2xl font-semibold text-gray-800 truncate">{title}</h1>
            </div>
            <div className="flex items-center space-x-4 flex-shrink-0">
              {/* L'email è troppo lunga per la barra mobile: da sm in su. */}
              <div className="hidden sm:block text-sm text-gray-600 max-w-[40vw] truncate">
                <span className="font-medium">{user.email}</span>
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
